import { Button } from '@/components/ui/button';
import { Form, FormDescription } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AppDataKeyField } from '@/form/AppDataKeyField';
import {
    AppCheckboxField,
    AppEquipmentTypeSelectField,
    AppImageField,
    AppInfoTypeSelectField,
    AppInputField,
    AppPromptsField,
    AppSelectField,
} from '@/form/FormUI';
import type { NodeFormProps, SelectNodeType } from '@/nodes';
import { equipmentTypesState, infoTypesState, useSavedFormValues } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { type EquipmentType, SelectDataSchema, type SelectDataType, type SpaceId } from '@rock/types';
import { CircleX, PlusCircleIcon } from 'lucide-react';
import { type FC, useCallback, useEffect } from 'react';
import {
    type FieldErrors,
    useFieldArray,
    type UseFieldArrayReturn,
    useForm,
    type UseFormReturn,
    useWatch,
} from 'react-hook-form';
import { toast } from 'sonner';
import type * as z from 'zod';

type FormInput = z.input<typeof SelectDataSchema>;

const SelectForm: FC<NodeFormProps<SelectNodeType>> = ({ spaceId, flowId, node, updateNode, cancelUpdate }) => {
    const infoTypes = useAsyncAtomValue(infoTypesState(spaceId));
    const equipmentTypes = useAsyncAtomValue(equipmentTypesState(spaceId));

    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, SelectDataType>({
        resolver: zodResolver(SelectDataSchema),
        defaultValues: formState || node.data,
    });

    const sections = useFieldArray({
        control: form.control,
        name: 'sections',
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const onSubmit = useCallback(
        (values: SelectDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <AppInputField
                        control={form.control}
                        name="pageTitle"
                        label="Page Title"
                        placeholder="Page Title"
                        description="The title displayed in the navigation header."
                    />

                    <AppPromptsField description="What are we asking the user to choose between?" />

                    <AppDataKeyField
                        control={form.control}
                        name="dataKey"
                        label="Data Key"
                        placeholder="field_name"
                        description="The name used to reference this value in code."
                        mode="produce"
                        currentNodeId={node.id}
                    />

                    <AppInfoTypeSelectField
                        control={form.control}
                        name="infoTypeId"
                        label="Info Type"
                        description="Store the selected option as the value of this Info Type."
                        infoTypes={infoTypes}
                        placeholder="Select Info Type (Optional)"
                    />

                    <AppSelectField
                        label="Help Type"
                        control={form.control}
                        name="helpType"
                        description="What kind of Help button to show."
                        options={['help-me-decide', 'model-number-help', 'none']}
                    />

                    {watchedFields.helpType === 'model-number-help' && (
                        <AppEquipmentTypeSelectField
                            control={form.control}
                            name="modelHelpEquipmentTypeId"
                            label="Equipment Type"
                            description="Select the Equipment Type that the user will get help finding the model number for"
                            equipmentTypes={equipmentTypes}
                            allowContextSelection
                        />
                    )}

                    <AppCheckboxField
                        control={form.control}
                        name="multi.enabled"
                        label="Multi-Select"
                        description="Whether the user can select muiltiple options."
                    />

                    {watchedFields.multi?.enabled && (
                        <AppInputField
                            control={form.control}
                            name="multi.buttonLabel"
                            label="Multi-Select Button"
                            placeholder="Next"
                            description="When multi-select is enabled, the label of the Next button."
                        />
                    )}

                    <div className="flex flex-col gap-2 items-stretch group/sections">
                        <Label>Sections</Label>
                        <FormDescription>
                            Sections can be used to group options together under a title. Leave blank if not needed.
                        </FormDescription>
                        {sections.fields.map((sectionField, sectionIndex) => (
                            <SectionForm
                                key={sectionField.id}
                                spaceId={spaceId}
                                flowId={flowId}
                                form={form}
                                sections={sections}
                                sectionIndex={sectionIndex}
                                equipmentTypes={equipmentTypes}
                            />
                        ))}
                        <Button
                            type="button"
                            onClick={() =>
                                sections.append({
                                    heading: '',
                                    options: [{ title: '', value: '', defaultSelected: false }],
                                })
                            }
                            variant="ghost"
                            size="sm"
                            className="cursor-pointer"
                        >
                            <PlusCircleIcon /> Add Section
                        </Button>
                    </div>
                </div>
                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};

const SectionForm: FC<{
    spaceId: SpaceId;
    flowId: string;
    form: UseFormReturn<z.input<typeof SelectDataSchema>, unknown, z.output<typeof SelectDataSchema>>;
    sections: UseFieldArrayReturn<z.input<typeof SelectDataSchema>, 'sections'>;
    sectionIndex: number;
    equipmentTypes: EquipmentType[] | undefined;
}> = ({ spaceId, flowId, form, sections, sectionIndex, equipmentTypes }) => {
    const options = useFieldArray({
        control: form.control,
        name: `sections.${sectionIndex}.options`,
    });
    return (
        <div className="relative group/section flex flex-col gap-3 m-3 p-3 border-1 border-dashed border-gray-400">
            <CircleX
                onClick={() => sections.remove(sectionIndex)}
                className="absolute top-0 right-0 m-1 opacity-0 max-md:opacity-100 group-hover/section:opacity-100 transition-opacity cursor-pointer translate-x-1/2 -translate-y-1/2 fill-white hover:fill-red-300"
            />
            <div className="flex flex-row justify-between items-start gap-3">
                <AppInputField
                    control={form.control}
                    name={`sections.${sectionIndex}.heading`}
                    label="Heading"
                    className="flex flex-row items-center gap-2"
                    smallLabel
                />
            </div>
            <OptionsForm
                spaceId={spaceId}
                flowId={flowId}
                form={form}
                sectionIndex={sectionIndex}
                options={options}
                equipmentTypes={equipmentTypes}
            />
        </div>
    );
};

const OptionsForm: FC<{
    spaceId: SpaceId;
    flowId: string;
    form: UseFormReturn<z.input<typeof SelectDataSchema>, unknown, z.output<typeof SelectDataSchema>>;
    sectionIndex: number;
    options: UseFieldArrayReturn<z.input<typeof SelectDataSchema>, `sections.${number}.options`>;
    equipmentTypes: EquipmentType[] | undefined;
}> = ({ spaceId, flowId, form, sectionIndex, options, equipmentTypes }) => {
    const multiEnabled = useWatch({ control: form.control, name: 'multi.enabled' });
    return (
        <div className="flex flex-col gap-2">
            <Label>Options</Label>
            {options.fields.map((optionField, optionIndex) => (
                <Popover key={optionField.id}>
                    <PopoverTrigger asChild>
                        <div className="cursor-pointer border border-gray-300 rounded-lg px-3 py-2 bg-white hover:bg-gray-100 transition group">
                            <span className="text-sm text-gray-700">
                                {form.getValues(`sections.${sectionIndex}.options.${optionIndex}.title`) ||
                                    'Untitled Option'}
                            </span>
                        </div>
                    </PopoverTrigger>
                    <PopoverContent
                        side="left"
                        align="start"
                        className="w-[300px] p-4 bg-white shadow-md border rounded-md"
                    >
                        <div className="flex flex-col gap-3">
                            <AppInputField
                                control={form.control}
                                name={`sections.${sectionIndex}.options.${optionIndex}.title`}
                                label="Title"
                                placeholder="Option Title"
                                className="gap-1"
                                smallLabel
                            />
                            <AppInputField
                                control={form.control}
                                name={`sections.${sectionIndex}.options.${optionIndex}.subtitle`}
                                label="Subtitle (Optional)"
                                placeholder="Examples or explanation..."
                                className="gap-1"
                                smallLabel
                            />
                            <AppInputField
                                control={form.control}
                                name={`sections.${sectionIndex}.options.${optionIndex}.value`}
                                label="Value"
                                smallLabel
                                placeholder="option_value"
                                className="gap-1"
                            />
                            <AppEquipmentTypeSelectField
                                smallLabel
                                control={form.control}
                                name={`sections.${sectionIndex}.options.${optionIndex}.equipmentTypeId`}
                                label="Equipment Type"
                                placeholder="Select Equipment Type"
                                description="Equipment Type associated with this option"
                                equipmentTypes={equipmentTypes}
                            />
                            {multiEnabled && (
                                <AppCheckboxField
                                    control={form.control}
                                    name={`sections.${sectionIndex}.options.${optionIndex}.defaultSelected`}
                                    label="Selected by default"
                                    smallLabel
                                />
                            )}
                            <AppImageField
                                form={form}
                                name={`sections.${sectionIndex}.options.${optionIndex}.bgImage`}
                                label="Background Image"
                                smallLabel
                                storagePathPrefix={`spaces/${spaceId}/flows/${flowId}/images`}
                            />
                            <div className="flex justify-end">
                                <Button
                                    type="button"
                                    variant="destructive"
                                    onClick={() => options.remove(optionIndex)}
                                    className="cursor-pointer"
                                >
                                    <CircleX /> Delete Option
                                </Button>
                            </div>
                        </div>
                    </PopoverContent>
                </Popover>
            ))}
            <div className="text-center">
                <Button
                    type="button"
                    onClick={() => options.append({ value: '', title: '', defaultSelected: false })}
                    variant="ghost"
                    size="sm"
                    className="cursor-pointer h-6 text-xs opacity-0 max-md:opacity-100 group-hover/section:opacity-100 transition-opacity"
                >
                    <PlusCircleIcon /> Add Option
                </Button>
            </div>
        </div>
    );
};

export default SelectForm;
